import "core-js/stable";
import "regenerator-runtime/runtime";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Markdown from 'react-markdown';
import { Howl } from 'howler';

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { EventEmitter } from 'events';
import classNames from 'classnames';
import useKey from 'use-key-hook';
import Select from 'react-select';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import localStorage from 'localforage';
import initReactFastclick from 'react-fastclick';

import { AssetManager } from './assetManager';
import { Scaler } from './scaler';
import pkg from '../package.json';
const { version: VERSION } = pkg;

const inkjs = require('inkjs');

import '@fortawesome/fontawesome-free/css/all.css'

const AMBIENT_VOLUME = 0.3;
const APP_WIDTH = 1920;
const APP_HEIGHT = 1080;
const COMMAND_PREFIX = '>> ';

function isDebug() {
  return window.location.search === "?debug"  
}

function debug(...args) {
  if (isDebug()) {
    console.log.apply(null, args);
  }
}

class StoryRunner extends EventEmitter {
  constructor({ assets = null, storyJson }) {
    super();

    this._state = {};
    this._backStack = [];
    this._forwardStack = [];
    this._sounds = [];
    this._toolbar = true;

    this._assets = assets || new AssetManager();
    this._reset();

    this._observers = [];

    this._story = new inkjs.Story(storyJson);

    this.go("start");
  }

  get assets() {
    return this._assets;
  }

  get state() {
    return {
      arrows: Object.assign({}, this._state.arrows),
      variables: this.variables,
      currentText: this.currentText,
      currentChoices: this.currentChoices,
      currentTags: this._story.currentTags,
      background: [...this._state.background],
      characters: [...this._state.characters],
      hotspots: [...this._state.hotspots],
      props: [...this._state.props],
      toolbar: this._state.toolbar,
    };
  }

  observeVariable(name, callback) {
    const item = { name, callback };
    this._observers.push(item);
    this._story.ObserveVariable(name, callback);

    return () => {
      this._observers = this._observers.filter(val => val !== item);
      this._story.RemoveVariableObserver(callback, name);
    }
  }

  _notifyVariableObservers() {
    for (const { name, callback } of this._observers) {
      callback(name, this.variables[name]);
    }
  }

  save() {
    const save = JSON.parse(JSON.stringify(this._state));
    save.story = this._story.state.ToJson();
    save.date = new Date();
    return save;
  }

  restore(save) {
    this._state = save;
    this._story.state.LoadJson(save.story);

    this._notifyChanged();
    this._notifyVariableObservers();
  }

  goBack() {
    if (this._backStack.length > 0) {
      this._forwardStack.push(this.save());
      this.restore(this._backStack.pop());
      debug(`(goBack) Back: ${this._backStack.length}, Forward: ${this._forwardStack.length}`);
    }
  }

  goForward() {
    if (this._forwardStack.length > 0) {
      this._backStack.push(this.save());
      this.restore(this._forwardStack.pop());
      debug(`(goForward) Back: ${this._backStack.length}, Forward: ${this._forwardStack.length}`);
    }
  }

  get variables() {
    return this._story.variablesState;
  }

  get currentText() {
    let text = this._story.currentText.trim();
    if (text.startsWith(COMMAND_PREFIX)) {
      return "";
    }

    const colonIndex = text.indexOf(':');
    if (colonIndex < 0) {
      return { text };
    }

    const varName = text.substring(0, colonIndex)
    text = text.substring(colonIndex + 1).trim();

    return { from: this._story.variablesState[varName], text };
  }

  get currentChoices() {
    return this._story.currentChoices.slice(0)
  }

  chooseChoiceIndex(index) {
    debug(`Choosing ${index}`);
    this._story.ChooseChoiceIndex(index);
    this.next();
  }

  _reset({ background, characters, hotspots, props, ambient, sounds, arrows } = { background: true, characters: true, hotspots: true, props: true, ambient: true, sounds: true, arrows: true }) {
    if (background) {
      this._state.background = [];
    }

    if (characters) {
      this._state.characters = [];
    }

    if (hotspots) {
      this._state.hotspots = [];
    }

    if (props) {
      this._state.props = [];
    }

    if (ambient && this._ambient) {
      this._ambient.stop();
      this._ambient.unload();
      this._ambient = null;
    }

    if (sounds && this._sounds) {
      for (const sound of this._sounds) {
        sound.stop();
        sound.unload();
      }

      this._sounds = [];
    }

    if (arrows) {
      this._state.arrows = {};
    }

    this._state.toolbar = true;
  }

  go(name) {
    debug(`Going to ${name}`);
    this._story.ChoosePathString(name);
    this.next();
  }

  get hasNext() {
    return this._story.canContinue;
  }

  async _processCommand(text) {
    const splitText = text.split(COMMAND_PREFIX);
    const [command, ...args] = splitText[1].trim().split(' ');

    debug('Command', command, 'Arguments', args);

    switch (command) {
      case 'bg':
        this._state.background = args;
        break;
      case 'char': {
        const charObj = {
          position: this._story.currentTags.pop() || "left",
          path: args
        }

        const idx = this._state.characters.findIndex(item => {
          return item.path[0] === charObj.path[0];
        });

        if (idx >= 0) {
          this._state.characters[idx] = charObj;
        } else {
          this._state.characters.push(charObj);
        }
        break;
      }
      case 'char-':
        // FIXME
        // delete this._state.characters[args.shift()];
        break;
      case 'debug':
        debug('>> debug ' + args.join(' '));
        break;
      case 'hotspot': {
        const [name, points] = args;
        this._state.hotspots.push({ name, points });
        break;
      }
      case 'reset':
        this._reset({
          background: args.length === 0 || args.includes('background'),
          characters: args.length === 0 || args.includes('characters'),
          hotspots: args.length === 0 || args.includes('hotspots'),
          props: args.length === 0 || args.includes('props'),
          ambient: args.length === 0 || args.includes('ambient'),
          sounds: args.length === 0 || args.includes('sounds'),
          arrows: args.length === 0 || args.includes('arrows')
        });
        break;
      case 'prop': {
        const path = args;
        let bounds = null;
        if (path[path.length - 1].includes(',')) {
          // Probably bounds
          bounds = args.pop();
        }
        this._state.props.push({ path, bounds });
        break;
      }
      case 'sound': {
        const asset = this._assets.find(['sfx'].concat(args));
        if (!asset) {
          break;
        }

        const { type, url } = asset;
        this._sounds.push(new Howl({
          src: [url],
          autoplay: true,
          format: [type]
        }));
        break;
      }
      case 'loop': {
        const asset = this._assets.find(['sfx'].concat(args));
        if (!asset) {
          break;
        }

        const { type, url } = asset;
        this._sounds.push(new Howl({
          src: [url],
          autoplay: true,
          loop: true,
          format: [type]
        }));
        break;
      }
      case 'ambient': {
        const url = this._assets.find(['sfx'].concat(args).url);

        if (this._ambient) {
          if (this._ambient._src === url) {
            break;
          }

          this._ambient.fade(AMBIENT_VOLUME, 0.0, 1000);
        }

        this._ambient = new Howl({
          src: [this._assets.find(['sfx'].concat(args)).url],
          autoplay: true,
          loop: true
        });

        this._ambient.fade(0.0, AMBIENT_VOLUME, 1000);
        break;
      }
      case 'shake': {
        return new Promise(resolve => {
          const main = document.getElementById('main');

          main.classList.remove('shake');
          main.classList.add('shake');

          main.addEventListener('animationend', () => {
            main.classList.remove('shake');
            resolve();
          }, { once: true });
        });
      }
      case 'delay': {
        if (this._skipping) {
          break;
        }
        this._notifyChanged();
        return new Promise(resolve => {
          setTimeout(resolve, parseInt(args.shift()) * 1000);
        });
      }
      case 'fade_out': {
        return new Promise(resolve => {
          const main = document.getElementById('main');

          main.classList.add('fadeOut');
          main.addEventListener('animationend', () => {
            resolve();
          }, { once: true });
        });
      }
      case 'fade_in': {
        return new Promise(resolve => {
          const main = document.getElementById('main');
          if (!main.classList.contains('fadeOut')) {
            resolve();
            return;
          }

          main.classList.remove('fadeOut');
          main.classList.remove('fadeIn');
          main.classList.add('fadeIn');

          main.addEventListener('animationend', () => {
            main.classList.remove('fadeIn');
            resolve();
          }, { once: true });
        });
      }
      case 'arrow_down': {
        this._state.arrows.down = args.pop() || null;
        break;
      }
      case 'arrow_right': {
        this._state.arrows.right = args.pop() || null;
        break;
      }
      case 'arrow_left': {
        this._state.arrows.left = args.pop() || null;
        break;
      }
      case 'arrow_up': {
        this._state.arrows.up = args.pop() || null;
        break;
      }
      case 'hide_toolbar': {
        this._state.toolbar = false;
        break;
      }
      case 'show_toolbar': {
        this._state.toolbar = true;
        break;
      }
      default:
        break;
    }
  }

  _notifyChanged() {
    if ((this._story.currentErrors || []).length > 0) {
      for (const error of this._story.currentErrors) {
        console.error(`ERROR: ${error}`);
      }
    }

    this.emit('changed', this.state);
  }

  blank() {
    this._story.ChoosePathString("blank");
    this._story.Continue();
    this._reset({ characters: true });
    this._notifyChanged();   
  }

  async skip() {
    this._skipping = true;
    for (;;) {
      if (this.state.currentChoices && this.state.currentChoices.length > 0) {
        break;
      }

      if (!await this.next()) {
        break;
      }
    }
    this._skipping = false;
    this._notifyChanged();
  }

  isCommand(text) {
    return text.startsWith(COMMAND_PREFIX);
  }

  get isSignificantState() {
    return this.currentText.text !== "" || this.currentChoices.length > 0;
  }

  async next() {
    this._forwardStack = [];

    if (!this.hasNext) {
      this.blank();
      return false;
    }

    let haveCommand = false;
    do {
      if (this.hasNext) {
        this._story.Continue();
      } else {
        this.blank();
        break;
      }

      haveCommand = this.isCommand(this._story.currentText);
      if (haveCommand) {
        await this._processCommand(this._story.currentText);
      }
    } while (haveCommand);

    if (!this.isSignificantState) {
      console.log('Not significant! Going to next!')
      return this.next();
    }

    this._backStack.push(this.save());

    if (!this._skipping) {
      this._notifyChanged();
    }

    return true;
  }

  get pathNames() {
    return Array.from(this._story.mainContentContainer.namedContent.values())
      .filter(item => item.turnIndexShouldBeCounted)
      .map(item => item.name)
      .sort();
  }
}

let runner = null;

function Modal({ onClick, children, hidden }) {
  const classes = classNames({
    modal: true,
    clickable: !!onClick,
    hidden
  });

  const clickHandler = e => {
    if (e.target === e.currentTarget) {
      onClick(e);
    }
  }

  return (
    <div className={classes} onClick={clickHandler}>
      {children}
    </div>
  );
}

function ModalDialogRenderer({ dialog, hidden }) {
  const next = () => runner.next();

  // We don't want to pass 'hidden' to the Modal -- needs to be clickable
  return <Modal onClick={next}><DialogRenderer onClick={next} dialog={dialog} hidden={hidden}/></Modal>;
}

function DialogRenderer({ onClick, dialog, hidden }) {
  const classes = classNames({
    'message-box': true,
    hidden
  });

  let from = null;
  let text = dialog.text;
  if (!text || text.length === 0) {
    return <div/>;
  }

  if (dialog.from) {
    from = <div className="from">{dialog.from}</div>;
  } else {
    text = `*${text}*`;
  }

  return (
    <div>
      <div onClick={onClick} className={classes}>
        {from}
        <Markdown source={text}/>
      </div>
    </div>
  );
}

function ChoicesRenderer({ dialog, choices, hidden }) {
  const els = choices.map(choice => {
    return (
      <div className="choice" key={choice.index} onClick={() => runner.chooseChoiceIndex(choice.index)}>
        {choice.text}
      </div>
    );
  });

  return (
    <Modal hidden={hidden}>
      <div className="choices">{els}</div>
      <DialogRenderer dialog={dialog}/>
    </Modal>
  );
}

function parseBoundsStyle(bounds) {
  const intBounds = bounds.split(',').map(coord => parseInt(coord));
  return {
    left: `${Math.min(intBounds[0], intBounds[2])}px`,
    top: `${Math.min(intBounds[1], intBounds[3])}px`,
    width: `${Math.max(intBounds[2] - intBounds[0], intBounds[0] - intBounds[2])}px`,
    height: `${Math.max(intBounds[3] - intBounds[1], intBounds[1] - intBounds[3])}px`
  };
}

function Hotspot({ name=null, points="", designing = false } = {}) {
  const hotspotClasses = classNames({
    hotspot: true,
    clickable: true,
    'hotspot-designing': designing,
  });

  if (designing) {
    document.location.hash = points;
  }

  const onClick = async e => {
    e.preventDefault();
    e.stopPropagation();
    if (!designing && name) {
      runner.go(name);
    }
  }

  return <polygon points={points} className={hotspotClasses} onClick={onClick}/>;
}

function HotspotsRenderer({ designing = false, hotspots = []} = {}) {
  const children = hotspots.map(({ name, points }, i) => {
    return <Hotspot key={name + i} designing={designing} name={name} points={points}/>;
  });

  return (
    <div className="layer">
      <svg style={{position: "absolute", top: 0, left: 0}} width={APP_WIDTH} height={APP_HEIGHT}>
        {children}
      </svg>
    </div>
  )
}

function HotspotDesigner() {
  const [displayed, setDisplayed] = useState(false);
  const [points, setPoints] = useState(null);

  useKey((key, e) => {
    if (key === 27) {
      setDisplayed(false);
      setPoints(null);
      return;
    }

    if (e.target.tagName !== "INPUT") {
      setDisplayed(!displayed);
      setPoints(null);
    }
  }, {
    detectKeys: ['d', 27]
  }, {
    dependencies: [displayed]
  });

  if (!displayed) {
    return <div/>;
  }

  const getTransformedPoint = function(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.pageX - rect.x) / Scaler.currentScale, 
      y: (e.pageY - rect.y) / Scaler.currentScale
    };
  }

  const onMouseDown = function(e) {
    const { x, y } = getTransformedPoint(e);
    if (points) {
      setPoints(`${points},${x},${y}`);
    } else {
      setPoints(`${x},${y}`);
    }
  }

  return (
    <div className="hotspot-designer" onMouseDown={onMouseDown}>
      <div className="title">Hotspot Edit Mode</div>
      <HotspotsRenderer designing={true} hotspots={[{ name: "designing", points }]}/>
    </div>
  );
}

function GotoHandler() {
  const [displayed, setDisplayed] = useState(false);

  useKey((key, e) => {
    if (!displayed) {
      // This stops 'g' from going into the input box when first displaying.
      e.preventDefault();
      e.stopPropagation();
    }

    if (key === 27) {
      setDisplayed(false);
      return;
    }

    if (!displayed && e.target.tagName !== "INPUT") {
      setDisplayed(true);
    }
  }, {
    detectKeys: ['g', 27]
  }, {
    dependencies: [displayed]
  });

  if (!displayed) {
    return <div/>;
  }

  const changeHandler = selection => {
    if (selection) {
      runner.go(selection.value);
      setDisplayed(false);
    }
  }

  const items = runner.pathNames.map(path => {
    return { value: path, label: path }
  });

  return (
    <div className="goto-container">
      <div style={{ width: '30%' }}>
        <Select
          placeholder=""
          options={items}
          autosize={true}
          isClearable={true}
          isSearchable={true}
          name="path"
          onChange={changeHandler}
          autoFocus/>
      </div>
    </div>
  );
}

function LayerRenderer({ asset, type, blur, position }) {
  const findResult = runner.assets.find(asset);
  if (!findResult) {
    return (
      <div className="layer">
        <div className="placeholder">Layer: {asset.join(' ')}</div>
      </div>
    );
  }

  const { type: assetType, url } = findResult;

  const classes = classNames({
    layer: true,
    blur,
    left: position === 'left',
    right: position === 'right',
    rleft: position === 'rleft',
    lright: position === 'lright',
    lmiddle: position === 'lmiddle',
    rmiddle: position === 'rmiddle',
    background: type === "background",
    character: type === "character",
  });

  let style = null;
  if (position && position.includes(',')) {
    style = parseBoundsStyle(position);
  }

  const media = assetType === "webm" || assetType === "mp4" ?
    <video src={url} style={style} loop autoPlay={true}/> :
    <img draggable="false" src={url} style={style}/>;

  return <div className={classes}>{media}</div>;
}

function ArrowRenderer({ position, knot }) {
  const onClick = async e => {
    e.preventDefault();
    e.stopPropagation();
    runner.go(knot);
  }

  return (
    <div className={`arrow-${position}`} onClick={onClick}>
      <i className={`fas fa-caret-${position}`}/>
    </div>);
}

function ToolbarRenderer({ variables }) {
  const days = variables["days"];
  const timeOfDay = variables["time_of_day"].toString();
  const dayOfWeek = variables["day_of_week"].toString();
  const money = variables["money"];

  return (
    <div className="toolbar">
      <div className="text">Day {days}, {dayOfWeek} {timeOfDay}</div>
      <div className="flex"/>
      <div className="text">${money}</div>
    </div>
  );
}

const DEFAULT_SAVES = [null, null, null, null, null, null]
const SAVE_GAME_PATH = `saved-games-${VERSION}`;
async function saveGame(slot) {
  const saves = await localStorage.getItem(SAVE_GAME_PATH) || DEFAULT_SAVES;
  saves[slot] = runner.save();
  await localStorage.setItem(SAVE_GAME_PATH, saves);
  debug(`Saved game to slot ${slot}`);
}

async function restoreGame(slot) {
  const saves = await localStorage.getItem(SAVE_GAME_PATH) || DEFAULT_SAVES;
  const save = saves[slot];
  if (save) {
    runner.restore(save);
    debug(`Restored game from slot ${slot}`);
  }
}

async function fetchSaves() {
  return (await localStorage.getItem(SAVE_GAME_PATH)) || DEFAULT_SAVES
}

function SavedGamesRenderer({ saving = false }) {
  const [saves, setSaves] = useState(DEFAULT_SAVES);

  useEffect(() => {
    (async function() {
      setSaves(await fetchSaves());
    }());
  }, []);

  const saveClicked = async (save, i) => {
    if (saving) {
      if (!save || confirm('Are you sure you want to overwrite the existing save?')) {
        await saveGame(i);
        setSaves(await fetchSaves());
      }
    } else if (confirm('Are you sure you want to load this game?')) {
      restoreGame(i);
    }
  }

  const saveItems = saves.map((save, i) => <li key={i} onClick={() => saveClicked(save, i)}>{save ? save.date.toString() : "Empty"}</li>);
  return (
    <ul className="pause-panel saved-games">
      {saveItems}
    </ul>
  );
}

function InventoryRenderer() {
  const items = Array.from(runner.variables["inventory"].keys()).map(key => {
    const name = JSON.parse(key)["itemName"].replace('_', ' ');
    return <li key={name}>{name}</li>;
  });
  return <div className="pause-panel"><ul className="info-panel">{items}</ul></div>;
}

function InfoRenderer() {
  return (
    <div className="pause-panel">
      <ul className="info-panel">
        <li>Energy: {runner.variables["energy"]}</li>
        <li>Intelligence: {runner.variables["intelligence"]}</li>
        <li>Strength: {runner.variables["strength"]}</li>
        <li>Stamina: {runner.variables["stamina"]}</li>
      </ul>
    </div>
  );
}

function KeyShortcutsRenderer() {
  return (
    <div className="pause-panel">
      <ul className="info-panel">
        <li>ESC - Pause</li>
        <li>TAB - Skip to next choice</li>
        <li>F - Full screen</li>
        <li>H - Hide dialog</li>
        <li>Mouse wheel - rollback/forward</li>
      </ul>
    </div>
  );
}

function VariablePopup({ variable, name }) {
  const [value, setValue] = useState(runner.variables[variable]);
  const [thumb, setThumb] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    return runner.observeVariable(variable, (_, newValue) => {
      debug(`${name} went from ${value} to ${newValue}`);
      setValue(newValue);

      if (newValue > value) {
        setThumb('👍');
      } else if (newValue < value) {
        setThumb('👎');
      } else {
        // no change
        return;
      }

      setVisible(true);
      setTimeout(() => setVisible(false), 2000);
    });
  }, [value, thumb, visible]);

  const classes = classNames({
    popup: true,
    'popup-hidden': !visible
  });

  return <div className={classes}>{name} {thumb}</div>;
}

function VariablePopups() {
  return (
    <div className="popups">
      <VariablePopup name="Strength" variable="strength"/>
      <VariablePopup name="Stamina" variable="stamina"/>
      <VariablePopup name="Energy" variable="energy"/>
      <VariablePopup name="Intelligence" variable="intelligence"/>
    </div>
  );
}

function PauseRenderer() {
  const [displayed, setDisplayed] = useState(false);

  useKey(key => {
    if (key === 27) {
      setDisplayed(!displayed);
      return;
    }
  }, {
    detectKeys: [27]
  }, {
    dependencies: [displayed]
  });

  if (!displayed) {
    return <div/>;
  }

  return (
    <Modal onClick={() => setDisplayed(false)}>
      <div className="pause-container">
        <Tabs>
          <TabList className="pause-tab-list">
            <Tab className="pause-tab" selectedClassName="pause-tab-selected">Save</Tab>
            <Tab className="pause-tab" selectedClassName="pause-tab-selected">Load</Tab>
            <Tab className="pause-tab" selectedClassName="pause-tab-selected">Inventory</Tab>
            <Tab className="pause-tab" selectedClassName="pause-tab-selected">Player</Tab>
            <Tab className="pause-tab" selectedClassName="pause-tab-selected">Keys</Tab>
          </TabList>

          <TabPanel>
            <SavedGamesRenderer saving={true}/>
          </TabPanel>
          <TabPanel>
            <SavedGamesRenderer saving={false}/>
          </TabPanel>
          <TabPanel>
            <InventoryRenderer/>
          </TabPanel>
          <TabPanel>
            <InfoRenderer/>
          </TabPanel>
          <TabPanel>
            <KeyShortcutsRenderer/>
          </TabPanel>
        </Tabs>
      </div>
    </Modal>
  );
}

function Game({ assets }) {
  let layers = [];
  let text = null;
  let choices = null;
  let hotspots = null;
  let arrows = [];

  if (!runner) {
    // GROSSSSS
    runner = window.runner = new StoryRunner({ assets, storyJson: require('./ink/main.ink') });
  }

  const [storyState, setStoryState] = useState(runner.state);
  useEffect(() => {
    function handleChange(val) {
      setStoryState(val);
    }

    runner.on('changed', handleChange);
    return () => {
      runner.off('changed', handleChange);
    }
  });

  const [hidden, setHidden] = useState(false);

  useKey(async (key, e) => {
    if (e.target.tagName === "INPUT") {
      return;
    }

    switch (e.key) {
    case 't':
      if (isDebug()) {
        runner.go('debug_pass_time');
      }
      break;
    case 's':
      if (isDebug()) {
        await saveGame(0);
      }
      break;
    case 'r':
      if (isDebug()) {
        await restoreGame(0);
      }
      break;
    case 'Tab':
      runner.skip();
      break;
    case 'h':
      setHidden(!hidden);
      break;
    case 'f':
      document.body.requestFullscreen();
      break;
    default:
      break;
    }

  }, {
    detectKeys: [9 /* Tab */, 't', 's', 'r', 'h', 'f']
  }, {
    dependencies: [hidden]
  });

  debug('Story State', storyState);
  let isNight = false;

  if (storyState) {
    const characters = storyState.characters;
    isNight = storyState.variables.time_of_day.toString() === 'night';

    if (storyState.currentChoices.length > 0) {
      choices = <ChoicesRenderer dialog={storyState.currentText} choices={storyState.currentChoices} hidden={hidden}/>;
    } else if (storyState.currentText.text) {
      text = <ModalDialogRenderer dialog={storyState.currentText} hidden={hidden}/>;
    }

    if (storyState.background.length > 0) {
      const key = storyState.background.join('_');
      layers.push(<CSSTransition key={key} timeout={500} classNames="fade">
        <LayerRenderer asset={['bg'].concat(storyState.background)} blur={characters.length > 0} type="background"/>
      </CSSTransition>);
    }

    if (storyState.props.length > 0) {
      for (const prop of storyState.props) {
        const key = prop.path.join('_');
        layers.push(<CSSTransition key={key} timeout={500} classNames="fade">
          <LayerRenderer asset={['props'].concat(prop.path)} position={prop.bounds}/>
        </CSSTransition>);
      }
    }

    if (characters.length > 0) {
      for (const { position, path } of characters) {
        const key = path.join('_');
        layers.push(<CSSTransition key={key} timeout={500} classNames="fade">
          <LayerRenderer key={key} asset={['characters'].concat(path)} position={position} type="character"/>
        </CSSTransition>);
      }
    }

    if (storyState.hotspots.length > 0) {
      hotspots = <HotspotsRenderer hotspots={storyState.hotspots}/>;
    }

    if (!text && !choices) {
      arrows = Object.entries(storyState.arrows).map(([position, knot]) => {
        return <ArrowRenderer key={position} position={position} knot={knot}/>;
      });
    }
  }

  let toolbar = null;
  if (storyState.toolbar) {
    toolbar = <ToolbarRenderer variables={storyState.variables}/>
  }

  let debugElements = null;
  if (isDebug()) {
    debugElements = (<>
      <HotspotDesigner key="designer"/><GotoHandler key="goto"/>
    </>);
  }

  let nightOverlay = null;
  if (isNight) {
    nightOverlay = <div className="layer night"/>;
  }

  return (
    <div>
      <TransitionGroup appear={true}>
        {layers}
      </TransitionGroup>
      {nightOverlay}
      {hotspots}
      {arrows}
      {text}
      {choices}
      {toolbar}
      <VariablePopups/>
      <PauseRenderer/>
      {debugElements}
    </div>
  );
}

function App() {
  const [assets, setAssets] = useState(null);

  useEffect(() => {
    const newAssets = new AssetManager();
    if (isDebug()) {
      return setAssets(newAssets);
    }

    newAssets.preload().then(() => setAssets(newAssets), e => {
      console.error('Failed to preload assets', e);
      setAssets(newAssets);
    });
  }, []);

  if (!assets) {
    return <div className="loading">Loading...</div>;
  }

  return <Game assets={assets}/>;
}

initReactFastclick();
const main = document.getElementById("main");
ReactDOM.render(<App/>, main);

main.addEventListener('wheel', e => {
  if (e.deltaY < 0) {
    runner.goBack();
  } else {
    runner.goForward();
  }
});

let lastTouchStart = null;
main.addEventListener('touchstart', e => {
  if (lastTouchStart && (e.timeStamp - lastTouchStart.timeStamp) < 250) {
    document.body.requestFullscreen();
  }

  lastTouchStart = e;
});

Scaler.start(main, APP_WIDTH, APP_HEIGHT);
